import { detectDominantLanguage } from '../../shared/language-direction'

/**
 * The translation pipeline is indexed by input *line*, end to end: the worker
 * asks the model for one entry per input line and caches one row per line;
 * the client then renders one result per line it sent. A line holding several
 * sentences muddies that contract — the model tends to answer per sentence,
 * and the merged-back answer is cached against the whole line.
 *
 * Splitting sentences onto their own lines before the query is sent keeps each
 * unit the model translates aligned with a line the pipeline can address.
 */

const TERMINATORS = new Set(['.', '!', '?', '…'])

// Punctuation that belongs to the sentence it closes, not to the next one.
const CLOSERS = new Set(['"', "'", '”', '’', '»', ')', ']'])

// Latin abbreviations that end in a period without ending a sentence. Forms
// carrying an interior dot (e.g., i.e., พ.ศ.) are caught by the generic rule
// in isSentenceEndingDot and don't need listing here.
const ABBREVIATIONS = new Set([
   'mr',
   'mrs',
   'ms',
   'dr',
   'prof',
   'rev',
   'sr',
   'jr',
   'st',
   'mt',
   'vs',
   'etc',
   'approx',
   'dept',
   'est',
   'fig',
   'no',
   'vol',
   'pp',
   'al',
   'inc',
   'ltd',
   'co',
   'univ',
   'jan',
   'feb',
   'mar',
   'apr',
   'jun',
   'jul',
   'aug',
   'sep',
   'sept',
   'oct',
   'nov',
   'dec',
])

const tokenBefore = (line: string, index: number): string => {
   const match = line.slice(0, index).match(/\S+$/)
   return match ? match[0].toLowerCase() : ''
}

/** Whether a lone '.' closes a sentence rather than trailing an abbreviation. */
const isSentenceEndingDot = (line: string, index: number): boolean => {
   const token = tokenBefore(line, index)
   if (!token) return false
   // An interior dot marks an abbreviation in either script: e.g., U.S., พ.ศ.
   if (token.includes('.')) return false
   // A single character before the dot is an initial: "J. R. R. Tolkien".
   if ([...token].length === 1) return false
   return !ABBREVIATIONS.has(token)
}

/**
 * Split one line into sentences. Returns the line unchanged (as a single
 * entry) when it holds no sentence boundary, so callers can map over lines
 * without special-casing.
 */
export const splitIntoSentences = (line: string): string[] => {
   // Thai doesn't delimit sentences with periods — a '.' in Thai text is
   // nearly always an abbreviation (พ.ศ., น.ส., กม.) — so only '!' and '?'
   // open a boundary there. Decided by the line's dominant script, not the
   // presence of any Thai at all: one Thai word inside an English paragraph
   // must not stop the paragraph splitting on '.'.
   const dotEndsSentences = detectDominantLanguage(line) === 'english'

   const sentences: string[] = []
   let start = 0
   let i = 0

   while (i < line.length) {
      if (!TERMINATORS.has(line[i])) {
         i += 1
         continue
      }

      // Consume a run of terminators ("...", "?!") as one boundary.
      let end = i
      while (end + 1 < line.length && TERMINATORS.has(line[end + 1])) end += 1

      let after = end + 1
      while (after < line.length && CLOSERS.has(line[after])) after += 1

      // A boundary needs whitespace and then more text: a terminator at the end
      // of the line closes the last sentence, which the tail below picks up.
      const whitespace = line.slice(after).match(/^\s+/)
      if (!whitespace || !line.slice(after + whitespace[0].length).trim()) {
         i = after
         continue
      }

      const isLoneDot = line[i] === '.' && end === i
      if (isLoneDot && (!dotEndsSentences || !isSentenceEndingDot(line, i))) {
         i = after
         continue
      }

      const sentence = line.slice(start, after).trim()
      if (sentence) sentences.push(sentence)
      start = after + whitespace[0].length
      i = start
   }

   const tail = line.slice(start).trim()
   if (tail) sentences.push(tail)

   return sentences.length ? sentences : [line]
}

/**
 * Break a query into the lines sent to the translation worker: existing line
 * breaks first, then a line per sentence. Blank lines are preserved as-is —
 * the worker keys its cache on non-blank lines and expects the positions to
 * line up.
 */
export const splitQueryIntoLines = (query: string): string[] =>
   query.split('\n').flatMap((line) => (line.trim() ? splitIntoSentences(line) : [line]))
